import type {
  AnswerResponse,
  ApiErrorEnvelope,
  ApiErrorPayload,
  AuditEventView,
  BookingView,
  ChallengeView,
  ConfirmResponse,
  DemoConfig,
  Session,
} from './types.ts';

/**
 * The single place this SPA talks to the server.
 *
 * Three properties are deliberate and worth keeping if this file is replaced.
 *
 * - `credentials: 'same-origin'`, and nothing is ever read from or written to
 *   `localStorage`. The session lives in an httpOnly cookie the browser cannot see, so
 *   there is no token here to steal via XSS, because there is no token here at all.
 * - The CSRF token is held in memory only and sent as a header. A cookie the browser
 *   sends automatically plus a header only this code can set is the pair that makes a
 *   cross-site form post fail.
 * - Non-2xx responses raise `ApiError`, carrying the server's `code`, `recoverability`
 *   and `correlationId`. The UI branches on `code`, never on message text.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }

  get code(): string {
    return this.payload.code;
  }

  get correlationId(): string {
    return this.payload.correlationId;
  }

  get recoverability(): ApiErrorPayload['recoverability'] {
    return this.payload.recoverability;
  }
}

/**
 * Responses that are not errors even though they are not 200.
 *
 * A wrong OTP is a 400 and a lockout is a 429, because they are not successes and a
 * generic client should not treat them as ones. But they are expected outcomes of the
 * flow with their own UI, so they are returned rather than thrown.
 */
const STEP_UP_ANSWER_STATUSES = new Set([200, 400, 410, 429]);

const CSRF_HEADER = 'x-csrf-token';

let csrfToken: string | undefined;

export function setCsrfToken(token: string | undefined): void {
  csrfToken = token;
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH';
  readonly body?: unknown;
  /** HTTP statuses to return rather than throw. */
  readonly allowStatuses?: ReadonlySet<number>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };

  if (options.body !== undefined) headers['content-type'] = 'application/json';
  // Sent on every mutating request. The server rejects a mismatch before doing work.
  if (method !== 'GET' && csrfToken) headers[CSRF_HEADER] = csrfToken;

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const text = await response.text();
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : {};

  if (response.ok || options.allowStatuses?.has(response.status)) {
    return parsed as T;
  }

  throw new ApiError(response.status, normalizeError(parsed, response));
}

/**
 * Every failure becomes an `ApiErrorPayload`, including ones the BFF never produced.
 *
 * The server nests its four permitted fields under `error` — that is `AppError`'s way of
 * making `internalDetail` and `stack` structurally absent rather than merely omitted, so
 * the envelope has to be unwrapped here. Reading `code` off the top level instead is a
 * silent failure: the payload parses, nothing throws, and the UI simply never sees the
 * code it branches on.
 *
 * A proxy timeout or an HTML error page from something in front of the API is handled by
 * the same path, because it would otherwise surface as a `SyntaxError` from `JSON.parse`
 * with no correlation id — the one moment in a demo where the UI must still say
 * something useful.
 */
function normalizeError(parsed: unknown, response: Response): ApiErrorPayload {
  const candidate = (parsed as Partial<ApiErrorEnvelope> | null)?.error;
  if (candidate && typeof candidate.code === 'string' && typeof candidate.message === 'string') {
    return {
      code: candidate.code,
      message: candidate.message,
      recoverability: candidate.recoverability ?? 'TERMINAL',
      correlationId: candidate.correlationId ?? response.headers.get('x-correlation-id') ?? '—',
    };
  }
  return {
    code: 'UNEXPECTED_RESPONSE',
    message: `The server returned ${response.status}. Try again, or restart the demo.`,
    recoverability: 'TERMINAL',
    correlationId: response.headers.get('x-correlation-id') ?? '—',
  };
}

export const api = {
  config: () => request<DemoConfig>('/api/config'),

  session: () => request<Session>('/api/session'),

  signIn: (email: string, password: string) =>
    request<Session>('/api/auth/login', { method: 'POST', body: { email, password } }),

  signOut: () => request<{ status: string }>('/api/auth/logout', { method: 'POST' }),

  bookings: () => request<{ bookings: BookingView[] }>('/api/bookings'),

  amend: (bookingId: string, changes: Record<string, unknown>) =>
    request<{ booking: BookingView }>(`/api/bookings/${bookingId}`, {
      method: 'PATCH',
      body: changes,
    }),

  /**
   * Confirm a booking, optionally presenting a grant.
   *
   * The 401 carrying `STEP_UP_REQUIRED` is an expected branch of the normal flow, not
   * an error: it is the server saying "this one needs verification". Note what is *not*
   * in this signature — no amount, no threshold, no "already verified" flag. The server
   * reads the amount from its own record, so there is nothing here to forge.
   */
  confirm: (bookingId: string, grantToken?: string) =>
    request<ConfirmResponse>(`/api/bookings/${bookingId}/confirm`, {
      method: 'POST',
      body: grantToken ? { grantToken } : {},
      allowStatuses: new Set([200, 401]),
    }),

  startChallenge: (transactionId: string) =>
    request<{ challenge: ChallengeView }>(`/api/step-up/${transactionId}/start`, {
      method: 'POST',
    }),

  /**
   * Ask for the code again.
   *
   * A separate call rather than a flag on `startChallenge`, mirroring the server: `start`
   * resumes and sends nothing, which is what makes a page reload safe. Every refusal —
   * cooldown, resend ceiling, delivery failure — arrives as an `ApiError`, so nothing here
   * is in `allowStatuses`: none of them is a step of the flow.
   */
  resendChallenge: (transactionId: string) =>
    request<{ challenge: ChallengeView }>(`/api/step-up/${transactionId}/resend`, {
      method: 'POST',
    }),

  answerChallenge: (transactionId: string, code: string) =>
    request<AnswerResponse>(`/api/step-up/${transactionId}/answer`, {
      method: 'POST',
      body: { code },
      allowStatuses: STEP_UP_ANSWER_STATUSES,
    }),

  audit: () => request<{ events: AuditEventView[] }>('/api/audit'),
};
